// Coach Portefeuille — page détail /investments/coach.
// Brief quantitatif complet : régime → garde-fous → shortlist (export CSV)
// → performance, + réglages utilisateur (CoachSetting).
//   GET   /api/investment/coach/brief/     -> dernier brief archivé | 404
//   POST  /api/investment/coach/brief/     -> (re)génère le brief du jour
//   GET   /api/investment/coach/settings/  -> paramètres du Coach
//   PATCH /api/investment/coach/settings/  -> mise à jour partielle
// Page multi-sections : on ne passe PAS par DetailPageLayout (pensé pour
// chart+table avec préférences devise/période), mais on reprend ses tokens
// (invTheme) et sa trame de header pour rester cohérent avec l'existant.
import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { makeGetRequest, makePatchRequest, makePostRequest } from 'utils/api'
import AssetTable from '../../_shared/AssetTable'
import { invTheme } from '../../_shared/theme'
import { numberCol, percentCol, moneyCol, textCol, toNum } from '../../_shared/gridCols'
import { REGIME_COLORS } from '../cards/CoachCard'

const SETTING_FIELDS = [
  'max_position_pct',
  'max_leverage_total_pct',
  'risk_per_trade_pct',
  'stop_fixe_pct',
  'atr_stop_mult',
  'min_median_volume',
  'shortlist_size',
]

// Section encadrée, même surface que les cards du dashboard.
function Section({ title, accent, extra, children }) {
  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: invTheme.cardRadius,
        border: invTheme.cardBorder,
        background: invTheme.cardBg,
        boxShadow: invTheme.cardShadow,
        position: 'relative',
        pl: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 16,
          bottom: 16,
          width: 3,
          borderRadius: 2,
          background: accent || invTheme.accents.market,
        },
      }}
    >
      <Box display="flex" alignItems="center" mb={1.5}>
        <Box sx={invTheme.title}>{title}</Box>
        {extra ? <Box sx={{ ml: 'auto' }}>{extra}</Box> : null}
      </Box>
      {children}
    </Box>
  )
}

function Metric({ label, value, color }) {
  return (
    <Box sx={{ mr: 3, mb: 1 }}>
      <Box sx={invTheme.subtitle}>{label}</Box>
      <Box sx={{ fontSize: 16, fontWeight: 700, color: color || 'white', mt: 0.25 }}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </Box>
    </Box>
  )
}

export default function CoachDetail() {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const [brief, setBrief] = useState(null)
  const [briefDate, setBriefDate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)
  const [settings, setSettings] = useState(null)
  const [settingsDraft, setSettingsDraft] = useState({})
  const [settingsMsg, setSettingsMsg] = useState(null)
  const [stopsSearch, setStopsSearch] = useState('')
  const [shortlistSearch, setShortlistSearch] = useState('')

  useEffect(() => {
    const load = async () => {
      try {
        const { data, status } = await makeGetRequest('/api/investment/coach/brief/', {}, true)
        if (status === 200 && data?.payload) {
          setBrief(data.payload)
          setBriefDate(data.date)
        }
      } catch (_) {
        /* 404 = pas encore de brief */
      }
      try {
        const { data, status } = await makeGetRequest('/api/investment/coach/settings/', {}, true)
        if (status === 200 && data) {
          setSettings(data)
          setSettingsDraft(data)
        }
      } catch (_) {
        /* silencieux */
      }
      setLoading(false)
    }
    load()
  }, [])

  const generate = async () => {
    if (generating) return
    setGenerating(true)
    setError(null)
    try {
      const { data } = await makePostRequest('/api/investment/coach/brief/', {}, {}, true)
      if (data?.payload) {
        setBrief(data.payload)
        setBriefDate(data.date)
      } else setError(t('investments.coach.card.error'))
    } catch (_) {
      setError(t('investments.coach.card.error'))
    } finally {
      setGenerating(false)
    }
  }

  const saveSettings = async () => {
    setSettingsMsg(null)
    const partial = {}
    SETTING_FIELDS.forEach((f) => {
      if (settingsDraft[f] !== settings?.[f]) partial[f] = settingsDraft[f]
    })
    if (Object.keys(partial).length === 0) return
    try {
      const { data, status } = await makePatchRequest(
        '/api/investment/coach/settings/',
        partial,
        {},
        true,
      )
      if (status === 200 && data) {
        setSettings(data)
        setSettingsDraft(data)
        setSettingsMsg({ ok: true, text: t('investments.coach.detail.saved') })
      } else {
        setSettingsMsg({ ok: false, text: t('investments.coach.detail.settingsError') })
      }
    } catch (_) {
      setSettingsMsg({ ok: false, text: t('investments.coach.detail.settingsError') })
    }
  }

  // Export CSV client-side de la shortlist (spec §3.2).
  const exportCsv = () => {
    const lignes = brief?.shortlist?.lignes || []
    const headers = [
      'ticker', 'nom', 'secteur', 'enveloppe', 'score_composite',
      'pts_qualite', 'pts_valo', 'pts_force', 'score_buffett', 'per',
      'pe_secteur', 'ret_3m', 'ret_6m', 'force_percentile', 'raison',
      'quantite_suggeree', 'stop_price',
    ]
    const esc = (v) => {
      if (v === null || v === undefined) return ''
      const s = String(v)
      return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const rows = lignes.map((l) =>
      [
        l.ticker, l.nom, l.secteur, l.enveloppe, l.score_composite,
        l.pts_qualite, l.pts_valo, l.pts_force, l.score_buffett, l.per,
        l.pe_secteur, l.ret_3m, l.ret_6m, l.force_percentile, l.raison,
        l.sizing?.quantite, l.sizing?.stop_price,
      ].map(esc).join(';'),
    )
    const csv = [headers.join(';'), ...rows].join('\n')
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `coach_shortlist_${briefDate || 'brief'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const regime = brief?.regime
  const accent = REGIME_COLORS[regime?.etat] || invTheme.accents.market
  const gardeFous = brief?.garde_fous
  const levier = gardeFous?.levier
  const shortlist = brief?.shortlist
  const perf = brief?.performance?.vs_benchmark
  const hitRate = brief?.performance?.hit_rate

  const stopsColumns = useMemo(
    () => [
      textCol({ field: 'nom', headerName: t('investments.coach.detail.cols.name'), flex: 1, minWidth: 160 }),
      textCol({ field: 'ticker', headerName: t('investments.coach.detail.cols.ticker'), width: 110 }),
      numberCol({ field: 'quantite', headerName: t('investments.coach.detail.cols.qty'), width: 90, decimals: 0 }),
      moneyCol({ field: 'prix_achat', headerName: t('investments.coach.detail.cols.pru'), width: 110 }),
      moneyCol({ field: 'cours', headerName: t('investments.coach.detail.cols.price'), width: 110 }),
      percentCol({ field: 'pnl_pct', headerName: t('investments.coach.detail.cols.pnl'), width: 100 }),
      textCol({ field: 'statut', headerName: t('investments.coach.detail.cols.status'), width: 130 }),
      textCol({ field: 'motif', headerName: t('investments.coach.detail.cols.motive'), flex: 1, minWidth: 180 }),
    ],
    [t],
  )

  const shortlistColumns = useMemo(
    () => [
      textCol({ field: 'ticker', headerName: t('investments.coach.detail.cols.ticker'), width: 100 }),
      textCol({ field: 'nom', headerName: t('investments.coach.detail.cols.name'), flex: 1, minWidth: 160 }),
      textCol({ field: 'secteur', headerName: t('investments.coach.detail.cols.sector'), width: 140 }),
      textCol({ field: 'enveloppe', headerName: t('investments.coach.detail.cols.envelope'), width: 100 }),
      numberCol({ field: 'score_composite', headerName: t('investments.coach.detail.cols.score'), width: 110 }),
      numberCol({ field: 'pts_qualite', headerName: t('investments.coach.detail.cols.quality'), width: 90 }),
      numberCol({ field: 'pts_valo', headerName: t('investments.coach.detail.cols.valo'), width: 90 }),
      numberCol({ field: 'pts_force', headerName: t('investments.coach.detail.cols.force'), width: 90 }),
      numberCol({ field: 'per', headerName: t('investments.coach.detail.cols.per'), width: 90 }),
      percentCol({ field: 'ret_3m', headerName: t('investments.coach.detail.cols.ret3m'), width: 100 }),
      percentCol({ field: 'ret_6m', headerName: t('investments.coach.detail.cols.ret6m'), width: 100 }),
      numberCol({
        field: 'sizing',
        headerName: t('investments.coach.detail.cols.sizing'),
        width: 130,
        decimals: 0,
        getter: (p) => toNum(p.row.sizing?.quantite),
      }),
      textCol({ field: 'raison', headerName: t('investments.coach.detail.cols.reason'), flex: 1.2, minWidth: 220 }),
    ],
    [t],
  )

  const hitRateColumns = useMemo(
    () => [
      textCol({ field: 'categorie', headerName: t('investments.coach.detail.cols.category'), flex: 1, minWidth: 130 }),
      numberCol({ field: 'horizon', headerName: t('investments.coach.detail.cols.horizon'), width: 100, decimals: 0 }),
      numberCol({ field: 'n', headerName: t('investments.coach.detail.cols.n'), width: 80, decimals: 0 }),
      percentCol({ field: 'rendement_moyen_pct', headerName: t('investments.coach.detail.cols.avg'), width: 110 }),
      percentCol({ field: 'rendement_median_pct', headerName: t('investments.coach.detail.cols.median'), width: 110 }),
      percentCol({ field: 'taux_reussite_pct', headerName: t('investments.coach.detail.cols.rate'), width: 120, decimals: 1 }),
    ],
    [t],
  )

  return (
    <Box sx={{ width: '100%', color: 'white' }}>
      <Container maxWidth="xl">
        {/* Header — trame DetailPageLayout : back + icône + titre + actions */}
        <Box display="flex" alignItems="center" sx={{ pt: 2, pb: 1.5 }}>
          <Tooltip title={t('investments.detail.back', 'Retour')}>
            <IconButton onClick={() => navigate('/investments')} sx={{ color: 'rgba(255,255,255,0.85)', mr: 1 }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              backgroundColor: 'rgba(255,255,255,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
              color: '#cfe1ff',
              flexShrink: 0,
            }}
          >
            <QueryStatsOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
              {t('investments.detail.pageTitle', 'Mes investissements')}
            </Box>
            <Box sx={{ ...invTheme.subtitle, mt: 0.5 }}>
              {t('investments.coach.card.title')}
              {briefDate ? ` · ${t('investments.coach.card.briefOf')} ${briefDate}` : ''}
            </Box>
          </Box>
          <Button
            onClick={generate}
            disabled={generating}
            variant="outlined"
            size="small"
            startIcon={
              generating ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <RefreshOutlinedIcon />
              )
            }
            sx={{
              ml: 'auto',
              textTransform: 'none',
              fontWeight: 600,
              color: invTheme.accents.market,
              borderColor: 'rgba(96,165,250,0.45)',
              '&:hover': {
                borderColor: invTheme.accents.market,
                backgroundColor: 'rgba(96,165,250,0.10)',
              },
            }}
          >
            {generating
              ? t('investments.coach.card.generating')
              : brief
                ? t('investments.coach.detail.refresh')
                : t('investments.coach.card.generate')}
          </Button>
        </Box>

        {/* Disclaimer lecture seule (fourni par le backend) */}
        {brief?.disclaimer && (
          <Box sx={{ mb: 2, fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
            {brief.disclaimer}
          </Box>
        )}
        {error && <Box sx={{ mb: 2, color: '#f87171', fontSize: 13 }}>{error}</Box>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: invTheme.accents.market }} />
          </Box>
        ) : !brief ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: invTheme.cardRadius,
              border: invTheme.cardBorder,
              background: invTheme.cardBg,
              color: 'rgba(255,255,255,0.7)',
              mb: 2,
            }}
          >
            {t('investments.coach.detail.noBrief')}
          </Box>
        ) : (
          <>
            {/* 1. Régime de marché */}
            <Section title={t('investments.coach.detail.regime')} accent={accent}>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                <Chip
                  label={regime?.etat || '—'}
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: '#001b42',
                    backgroundColor: accent,
                  }}
                />
                <Box sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
                  {regime?.buy_allowed
                    ? t('investments.coach.detail.buyAllowed')
                    : t('investments.coach.detail.buyBlocked')}
                </Box>
              </Stack>
              <Box display="flex" flexWrap="wrap">
                <Metric
                  label={t('investments.coach.detail.riskMultiplier')}
                  value={regime?.risk_multiplier !== undefined ? `×${regime.risk_multiplier}` : null}
                />
                <Metric
                  label={t('investments.coach.detail.indexVsEma')}
                  value={regime?.index_vs_ema200_pct !== null && regime?.index_vs_ema200_pct !== undefined
                    ? `${regime.index_vs_ema200_pct > 0 ? '+' : ''}${regime.index_vs_ema200_pct} %`
                    : null}
                  color={regime?.index_vs_ema200_pct >= 0 ? invTheme.positive : invTheme.negative}
                />
                <Metric
                  label={t('investments.coach.detail.slope')}
                  value={regime?.ema200_slope_pct !== null && regime?.ema200_slope_pct !== undefined
                    ? `${regime.ema200_slope_pct > 0 ? '+' : ''}${regime.ema200_slope_pct} %`
                    : null}
                  color={regime?.ema200_slope_pct >= 0 ? invTheme.positive : invTheme.negative}
                />
                <Metric
                  label={t('investments.coach.detail.breadth')}
                  value={regime?.breadth_pct !== null && regime?.breadth_pct !== undefined
                    ? `${regime.breadth_pct} %`
                    : null}
                />
              </Box>
              <Box sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55 }}>
                {regime?.commentaire}
              </Box>
            </Section>

            {/* 2. Garde-fous : stops + levier */}
            <Section title={t('investments.coach.detail.guardrails')} accent="#ef4444">
              <Box display="flex" flexWrap="wrap">
                <Metric
                  label={t('investments.coach.detail.capital')}
                  value={gardeFous?.capital !== null && gardeFous?.capital !== undefined
                    ? `${Number(gardeFous.capital).toLocaleString('fr-FR')} €`
                    : null}
                />
                <Metric
                  label={t('investments.coach.detail.leverage')}
                  value={levier?.exposition_pct !== null && levier?.exposition_pct !== undefined
                    ? `${levier.exposition_pct} % (${Number(levier.exposition_eur || 0).toLocaleString('fr-FR')} €)`
                    : null}
                  color={levier?.alerte ? invTheme.negative : invTheme.positive}
                />
                <Metric
                  label={t('investments.coach.detail.threshold')}
                  value={levier?.seuil_pct !== undefined ? `${levier.seuil_pct} %` : null}
                />
              </Box>
              {levier?.alerte && (
                <Box sx={{ mb: 1.5, fontSize: 13, color: '#f87171', fontWeight: 600 }}>
                  ⚠️ {t('investments.coach.card.leverageAlert')}
                </Box>
              )}
              <Box sx={{ ...invTheme.subtitle, mb: 1 }}>
                {t('investments.coach.detail.stopsTitle')} ({(gardeFous?.stops || []).length})
              </Box>
              <AssetTable
                rows={(gardeFous?.stops || []).map((s, i) => ({ id: `${s.ticker || s.nom}-${i}`, ...s }))}
                columns={stopsColumns}
                emptyLabel={t('investments.portfolio.noInvestmentsYet')}
                searchable
                search={stopsSearch}
                onSearchChange={setStopsSearch}
              />
            </Section>

            {/* 3. Shortlist de candidats + export CSV */}
            <Section
              title={`${t('investments.coach.detail.shortlist')} (${(shortlist?.lignes || []).length}/${shortlist?.total_filtres ?? '—'})`}
              accent={invTheme.accents.opportunity}
              extra={
                <Button
                  size="small"
                  onClick={exportCsv}
                  disabled={(shortlist?.lignes || []).length === 0}
                  startIcon={<DownloadOutlinedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.75)',
                    '&:hover': { color: invTheme.accents.opportunity },
                  }}
                >
                  {t('investments.coach.detail.exportCsv')}
                </Button>
              }
            >
              <Box sx={{ mb: 1.5, fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                {shortlist?.message}
              </Box>
              <AssetTable
                rows={(shortlist?.lignes || []).map((l, i) => ({ id: l.ticker || i, ...l }))}
                columns={shortlistColumns}
                emptyLabel={t('investments.coach.card.shortlistEmpty')}
                searchable
                search={shortlistSearch}
                onSearchChange={setShortlistSearch}
              />
            </Section>

            {/* 4. Performance : vs benchmark + hit-rate */}
            <Section title={t('investments.coach.detail.performance')} accent={invTheme.accents.target}>
              <Box sx={{ ...invTheme.subtitle, mb: 1 }}>
                {t('investments.coach.detail.vsBenchmark')}
              </Box>
              <Box display="flex" flexWrap="wrap" alignItems="center">
                <Metric
                  label={t('investments.coach.detail.portfolioReturn')}
                  value={perf?.perf_portefeuille_pct !== null && perf?.perf_portefeuille_pct !== undefined
                    ? `${perf.perf_portefeuille_pct > 0 ? '+' : ''}${perf.perf_portefeuille_pct} %`
                    : null}
                />
                <Metric
                  label={t('investments.coach.detail.benchmarkReturn')}
                  value={perf?.perf_benchmark_pct !== null && perf?.perf_benchmark_pct !== undefined
                    ? `${perf.perf_benchmark_pct > 0 ? '+' : ''}${perf.perf_benchmark_pct} %`
                    : null}
                />
                <Metric
                  label={t('investments.coach.detail.alpha')}
                  value={perf?.alpha_pct !== null && perf?.alpha_pct !== undefined
                    ? `${perf.alpha_pct > 0 ? '+' : ''}${perf.alpha_pct} pt`
                    : null}
                  color={perf?.alpha_pct >= 0 ? invTheme.positive : invTheme.negative}
                />
                {perf?.verdict && (
                  <Chip
                    label={perf.verdict}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      color: 'white',
                      backgroundColor: perf.alpha_pct >= 0 ? invTheme.positiveSoft : invTheme.negativeSoft,
                      border: `1px solid ${perf.alpha_pct >= 0 ? invTheme.positive : invTheme.negative}`,
                    }}
                  />
                )}
              </Box>
              <Box sx={{ mb: 2, fontSize: 12.5, color: 'rgba(255,255,255,0.6)' }}>
                {perf?.commentaire}
              </Box>
              <Box sx={{ ...invTheme.subtitle, mb: 1 }}>
                {t('investments.coach.detail.hitRate')} ({hitRate?.evalues ?? 0}/{hitRate?.total_signaux ?? 0})
              </Box>
              <AssetTable
                rows={(hitRate?.categories || []).map((c, i) => ({ id: `${c.categorie}-${c.horizon}-${i}`, ...c }))}
                columns={hitRateColumns}
                emptyLabel={t('investments.coach.detail.noBrief')}
                minHeight={140}
              />
              {hitRate?.note && (
                <Box sx={{ mt: 1, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{hitRate.note}</Box>
              )}
            </Section>
          </>
        )}

        {/* 5. Réglages du Coach (toujours affichés, même sans brief) */}
        {settings && (
          <Section title={t('investments.coach.detail.settings')} accent={invTheme.accents.advice}>
            <Grid container spacing={2}>
              {SETTING_FIELDS.map((f) => (
                <Grid item xs={12} sm={6} md={3} key={f}>
                  <TextField
                    size="small"
                    fullWidth
                    type="number"
                    label={t(`investments.coach.detail.fields.${f}`)}
                    value={settingsDraft[f] ?? ''}
                    onChange={(e) => setSettingsDraft((d) => ({ ...d, [f]: e.target.value }))}
                    InputProps={{ sx: { color: 'white', fontSize: 13 } }}
                    InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)', fontSize: 13 } }}
                    sx={{
                      '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(167,139,250,0.5)' },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            <Box display="flex" alignItems="center" mt={2}>
              <Button
                onClick={saveSettings}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: invTheme.accents.advice,
                  borderColor: 'rgba(167,139,250,0.45)',
                  '&:hover': {
                    borderColor: invTheme.accents.advice,
                    backgroundColor: 'rgba(167,139,250,0.10)',
                  },
                }}
              >
                {t('investments.coach.detail.save')}
              </Button>
              {settingsMsg && (
                <Box sx={{ ml: 2, fontSize: 12.5, color: settingsMsg.ok ? invTheme.positive : '#f87171' }}>
                  {settingsMsg.text}
                </Box>
              )}
            </Box>
          </Section>
        )}

        {/* Footer disclaimer */}
        {brief?.footer && (
          <Box sx={{ mb: 4, fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>
            {brief.footer}
          </Box>
        )}
      </Container>
    </Box>
  )
}
